import Flex from '@Components/Bases/Flex';
import DeleteModal from '@Components/Modal/DeleteModal';
import theme from '@Components/Themes/Theme';
import UploadImageFormFields from '@Containers/UploadImageFormFields';
import { useDisclosure } from '@hooks/useDisclosure';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  InputLabel,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useGoalAndOperationalPlansForm } from '../../GoalAndOperationalPlansFormProvider';
import { IRelateMasterDataFormValues } from '../../GoalAndOperationalPlansFormProvider/types';
import RiskManagement from '../RiskManagement';
import Goals from './Goals/Goals';
import RelatedTargetModal from './RelatedTargetModal/RelatedTargetModal';
import TargetDetails from './TargetDetails';
import ValueUpPlanNameFormFields from './ValueUpPlanNameFormFields';
import ValueUpPlanSignificanceFormFields from './ValueUpPlanSignificanceFormFields';

const GoalAndOperationalPlansForm = () => {
  const {
    setValue,
    reset,
    getValues,
    formState: { errors },
    trigger,
  } = useGoalAndOperationalPlansForm();

  const { fields, append, remove } = useFieldArray({
    name: 'companyValueIncreasePlans.valueUpPlans',
  });

  const goalsError = errors.companyValueIncreasePlans?.valueUpPlans;

  const completedSection = useWatch({
    name: 'completedSection',
  });

  const { isCompletedTh, isCompletedEn } = completedSection || {};

  const [indexSelected, setIndexSelected] = useState<number>(0);

  const [
    openDeleteModal,
    { open: openDeleteModalDialog, close: closeDeleteModal },
  ] = useDisclosure();

  const [
    openTargetTopicsModal,
    { open: openTargetTopicsModalDialog, close: closeTargetTopicsModal },
  ] = useDisclosure();

  if (fields.length === 0) {
    append({});
  }

  const handleAddGoal = () => {
    append({});
  };

  const handleOpenDeleteModal = (index: number) => {
    setIndexSelected(index);
    openDeleteModalDialog();
  };

  const handleInfoButtonClick = (index: number) => {
    setIndexSelected(index);
    openTargetTopicsModalDialog();
  };

  const handleUpdateGoals = (
    planIndex: number,
    data: IRelateMasterDataFormValues[]
  ) => {
    setValue(
      `companyValueIncreasePlans.valueUpPlans.${planIndex}.relateGoals`,
      data
    );
    trigger(`companyValueIncreasePlans.valueUpPlans.${planIndex}.relateGoals`);
    closeTargetTopicsModal();
  };

  return (
    <>
      {fields.map((item, planIndex) => (
        <Accordion
          key={item.id}
          defaultExpanded={true}
          sx={{
            boxShadow: 'none',
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: '4px',
            mb: 2,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'black' }} />}
            aria-controls={`${item.id || planIndex}-content`}
            id={`${item.id || planIndex}-header`}
          >
            <Typography
              fontWeight={600}
              sx={{ flexGrow: 1 }}
              alignContent={'center'}
            >
              {`แผนการเพิ่มมูลค่าองค์กร และเป้าหมายที่ ${planIndex + 1}`}
            </Typography>
            {fields.length > 1 && (
              <IconButton
                size="small"
                edge="end"
                aria-label="delete"
                color="inherit"
                onClick={() => handleOpenDeleteModal(planIndex)}
                disabled={isCompletedTh || isCompletedEn}
              >
                <DeleteOutlined style={{ fontSize: '16px' }} />
              </IconButton>
            )}
          </AccordionSummary>
          <AccordionDetails>
            <ValueUpPlanNameFormFields
              planIndex={planIndex}
              isCompletedTh={isCompletedTh}
              isCompletedEn={isCompletedEn}
            />
            <Flex gap={4} my={4} alignItems={'flex-start'}>
              <InputLabel
                sx={{
                  textAlign: 'right',
                  minWidth: 350,
                }}
                required
              >
                หัวข้อที่เกี่ยวข้อง
              </InputLabel>
              <Flex flexWrap={'wrap'}>
                <Flex flexDirection={'column'}>
                  <TargetDetails planIndex={planIndex} />
                  <Button
                    color="inherit"
                    onClick={() => handleInfoButtonClick(planIndex)}
                    disabled={isCompletedTh || isCompletedEn}
                    sx={{
                      width: 'fit-content',
                    }}
                  >
                    กรุณาเลือกหัวข้อที่เกี่ยวข้อง
                  </Button>
                  {goalsError?.[planIndex]?.relateGoals && (
                    <Typography
                      mt={0.5}
                      variant={'caption'}
                      color={'error.main'}
                    >
                      {goalsError?.[planIndex]?.relateGoals?.message}
                    </Typography>
                  )}
                </Flex>
              </Flex>
            </Flex>
            <ValueUpPlanSignificanceFormFields
              planIndex={planIndex}
              isCompletedTh={isCompletedTh}
              isCompletedEn={isCompletedEn}
            />
            <Goals planIndex={planIndex} />
            <UploadImageFormFields
              title={'รูปภาพเป้าหมาย'}
              name={`companyValueIncreasePlans.valueUpPlans.${planIndex}`}
              disabledTh={isCompletedTh}
              disabledEn={isCompletedEn}
            />
            <RiskManagement planIndex={planIndex} />
          </AccordionDetails>
        </Accordion>
      ))}
      {openTargetTopicsModal && (
        <RelatedTargetModal
          opened={openTargetTopicsModal}
          close={closeTargetTopicsModal}
          onUpdateGoals={(data: IRelateMasterDataFormValues[]) => {
            handleUpdateGoals(indexSelected, data);
          }}
          planIndex={indexSelected}
        />
      )}

      <DeleteModal
        topic={`แผนการเพิ่มมูลค่าองค์กร และเป้าหมายที่ ${indexSelected + 1}`}
        opened={openDeleteModal}
        close={closeDeleteModal}
        confirm={() => {
          remove(indexSelected);
          reset({
            companyValueIncreasePlans: {
              valueUpPlans: getValues().companyValueIncreasePlans.valueUpPlans,
            },
            completedSection: {
              isCompletedTh,
              isCompletedEn,
            },
          });
          closeDeleteModal();
        }}
      />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
      >
        <Divider sx={{ flex: 1, borderColor: 'lightgray' }} />
        <Button
          color={'inherit'}
          size={'small'}
          startIcon={<AddRoundedIcon />}
          onClick={handleAddGoal}
          disabled={isCompletedTh || isCompletedEn}
        >
          เพิ่มแผนการเพิ่มมูลค่าองค์กร และเป้าหมาย
        </Button>
        <Divider sx={{ flex: 1, borderColor: 'lightgray' }} />
      </Box>
    </>
  );
};

export default GoalAndOperationalPlansForm;
